import { Component, ChangeDetectionStrategy, DoCheck, HostListener, ViewChild, inject } from '@angular/core';
import { AbstractControl, FormArray, FormControl, FormGroup } from '@angular/forms';
import { Router } from '@angular/router';
import { Observable, from } from 'rxjs';
import { finalize } from 'rxjs/operators';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { NgxSpinnerService } from 'ngx-spinner';
import { TranslateService } from '@ngx-translate/core';

import { COUNTRY_OPTIONS } from '../../creditor/country-options';
import { CountryOption } from '../../interfaces/countryoption.interface';
import { NorwayCaseSummary } from '../../interfaces/norwayCaseSummary.interface';
import { NorwaySubmissionSummary, SummaryRow, SummarySection } from '../../interfaces/norwaySubmissionSummary.interface';
import { FileUploadComponent } from '../../file/fileUpload/file-upload/file-upload.component';
import { ConfirmLeaveFormModalComponent } from '../../modal/confirm-leave-form.modal.component';
import { ErrorModalService } from '../../modal/error.modal.service';
import { fadeInAnimation } from '../../_animations/fade-in.animation';
import { NorgeApplicationFormService, declarationControlName } from './norge-application-form.service';
import { NorgeApplicationService } from '../norge-application.service';

const errorKeysByValidator: Record<string, string> = {
  required: 'validation.field-required',
  email: 'label.ogiltig-epost',
  trailingDot: 'validation.email-trailing-dot',
  invalidTestDomain: 'validation.email-test-domain-not-allowed',
  invalidBic: 'norge.soknad.validering.bic',
  min: 'norge.soknad.validering.belop',
  max: 'norge.soknad.validering.prosent',
  maxAmount: 'norge.soknad.validering.belop-for-stort',
  minlength: 'norge.soknad.validering.postnummer',
  maxlength: 'norge.soknad.validering.postnummer',
  isoDate: 'norge.soknad.validering.dato',
  invalidIban: 'norge.soknad.validering.iban',
  norwegianBankAccount: 'norge.soknad.validering.kontonummer',
  norwegianNationalIdentityNumber: 'norge.soknad.validering.fodselsnummer'
};

const allowedAttachmentExtensions = ['.pdf', '.jpg', '.jpeg', '.png'];
const maxAttachmentBytes = 10 * 1024 * 1024;
const isoDatePattern = /^(\d{4})-(\d{2})-(\d{2})$/;
const addressFieldKeysWithForeignVariant = ['postboksNrSoker', 'postnrSoker', 'poststedSoker'];

@Component({
    selector: 'app-norge-application',
    templateUrl: './norge-application.component.html',
    styleUrls: ['../../creditor/creditor-claim-form/creditor-claim-form.component.scss'],
    animations: [fadeInAnimation],
    providers: [NorgeApplicationFormService],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class NorgeApplicationComponent implements DoCheck {
  public readonly countries: CountryOption[] = COUNTRY_OPTIONS;
  public isCountryDropdownOpen = false;
  public countryFilterTerm = '';
  public activeCountryIndex = -1;

  @ViewChild(FileUploadComponent) fileUpload!: FileUploadComponent;

  private isSubmitted = false;
  private appliedCaseId: string | null = null;

  private readonly formService = inject(NorgeApplicationFormService);
  private readonly norgeApplicationService = inject(NorgeApplicationService);
  private readonly modalService = inject(NgbModal);
  private readonly router = inject(Router);
  private readonly spinner = inject(NgxSpinnerService);
  private readonly errorModalService = inject(ErrorModalService);
  private readonly translate = inject(TranslateService);

  public get form(): FormGroup {
    return this.formService.form;
  }

  public get selectedCase(): NorwayCaseSummary | null {
    return this.norgeApplicationService.selectedCase;
  }

  public get attachmentErrorKey(): string | null {
    const attachments = this.fileUpload?.files ?? [];

    if (attachments.some(attachment => !allowedAttachmentExtensions.some(extension => attachment.name.toLowerCase().endsWith(extension)))) {
      return 'norge.soknad.validering.vedlegg-type';
    }

    if (attachments.some(attachment => attachment.size > maxAttachmentBytes)) {
      return 'norge.soknad.validering.vedlegg-storrelse';
    }

    return null;
  }

  public get summarySections(): SummarySection[] {
    return Object.keys(this.form.controls)
      .map(panelName => ({
        titleKey: `norge.soknad.sektion.${panelName}`,
        rows: this.buildSummaryRows(this.control(panelName).value)
      }))
      .filter(summarySection => summarySection.rows.length > 0);
  }

  public get selectedCountryCode(): string {
    return this.control('personopplysninger.landSoker').value;
  }

  public get selectedCountry(): CountryOption | undefined {
    return this.countries.find(country => country.code === this.selectedCountryCode);
  }

  public get filteredCountries(): CountryOption[] {
    const term = this.countryFilterTerm.trim().toLowerCase();

    if (!term) {
      return this.countries;
    }

    return this.countries.filter(country =>
      country.name.toLowerCase().includes(term) ||
      country.code.toLowerCase().includes(term)
    );
  }

  public get addressVariant(): string | null {
    return this.formService.addressVariant(this.control('personopplysninger').getRawValue());
  }

  public control(path: string): AbstractControl {
    return this.form.get(path) as AbstractControl;
  }

  public array(path: string): FormArray {
    return this.form.get(path) as FormArray;
  }

  public addRow(path: string): void {
    const arrayName = path.split('.').pop() as string;
    this.array(path).push(this.formService.createRow(arrayName));
    this.form.markAsDirty();
  }

  public removeRow(path: string, index: number): void {
    this.array(path).removeAt(index);
    this.form.markAsDirty();
  }

  public isOptionChecked(path: string, option: string): boolean {
    return (this.control(path).value as string[]).includes(option);
  }

  public toggleOption(path: string, option: string): void {
    const selectedOptions = this.control(path).value as string[];
    const updatedOptions = selectedOptions.includes(option)
      ? selectedOptions.filter(selectedOption => selectedOption !== option)
      : [...selectedOptions, option];

    (this.control(path) as FormControl).setValue(updatedOptions);
    this.control(path).markAsDirty();
    this.control(path).markAsTouched();
  }

  public fieldErrorKey(control: AbstractControl | null): string | null {
    if (!control || !control.touched || !control.errors) {
      return null;
    }

    const validatorName = Object.keys(control.errors)[0];

    return errorKeysByValidator[validatorName] ?? 'norge.soknad.validering.ogiltig';
  }

  public openCountryDropdown(): void {
    this.isCountryDropdownOpen = true;
    this.countryFilterTerm = '';
    this.activeCountryIndex = this.countries.findIndex(country => country.code === this.selectedCountryCode);

    if (this.activeCountryIndex < 0) {
      this.activeCountryIndex = 0;
    }

    setTimeout(() => {
      const countryInput = document.querySelector('.country-combobox__input') as HTMLInputElement | null;
      countryInput?.focus();
    });
  }

  public onCountrySearch(value: string): void {
    this.countryFilterTerm = value;
    this.activeCountryIndex = this.filteredCountries.length > 0 ? 0 : -1;
  }

  public selectCountry(country: CountryOption): void {
    const landControl = this.control('personopplysninger.landSoker');
    landControl.setValue(country.code);
    landControl.markAsDirty();
    landControl.markAsTouched();

    this.isCountryDropdownOpen = false;
    this.countryFilterTerm = '';
    this.activeCountryIndex = -1;
  }

  public onCountryBlur(): void {
    setTimeout(() => {
      this.isCountryDropdownOpen = false;
      this.countryFilterTerm = '';
      this.activeCountryIndex = -1;
    }, 150);
  }

  public onCountryKeydown(event: KeyboardEvent): void {
    const countries = this.filteredCountries;

    if (!countries.length) {
      if (event.key === 'Escape') {
        this.isCountryDropdownOpen = false;
        this.countryFilterTerm = '';
        this.activeCountryIndex = -1;
      }
      return;
    }

    if (event.key === 'ArrowDown') {
      event.preventDefault();
      this.activeCountryIndex = Math.min(this.activeCountryIndex + 1, countries.length - 1);
      return;
    }

    if (event.key === 'ArrowUp') {
      event.preventDefault();
      this.activeCountryIndex = Math.max(this.activeCountryIndex - 1, 0);
      return;
    }

    if (event.key === 'Enter') {
      event.preventDefault();

      if (this.activeCountryIndex >= 0) {
        this.selectCountry(countries[this.activeCountryIndex]);
      }
      return;
    }

    if (event.key === 'Escape') {
      this.isCountryDropdownOpen = false;
      this.countryFilterTerm = '';
      this.activeCountryIndex = -1;
    }
  }

  public getCountryFlagClass(countryCode: string | null | undefined): string {
    if (!countryCode) {
      return '';
    }

    return `flag-icon flag-icon-${countryCode.toLowerCase()} flag-icon-squared`;
  }

  public trackByCountryCode(countryIndex: number, country: CountryOption): string {
    return country.code;
  }

  public ngDoCheck(): void {
    const caseId = this.selectedCase?.caseId ?? null;

    if (caseId === this.appliedCaseId) {
      return;
    }

    this.appliedCaseId = caseId;
    this.control('arbeidsgiver.hvaErBoetsNavn').setValue(this.selectedCase?.companyName ?? '');
  }

  public submit(): void {
    this.form.markAllAsTouched();

    if (this.form.invalid || this.attachmentErrorKey) {
      setTimeout(() => {
        const firstError = document.querySelector('.text-danger');
        firstError?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      });
      return;
    }

    const application = {
      language: this.translate.getCurrentLang() || 'nb',
      caseId: this.selectedCase?.caseId,
      ...this.form.value
    };

    const summary: NorwaySubmissionSummary = {
      caseName: this.selectedCase ? `${this.selectedCase.companyName} (${this.selectedCase.orgNr})` : '',
      sections: this.summarySections,
      attachmentNames: (this.fileUpload?.files ?? []).map(attachment => attachment.name)
    };

    this.spinner.show();

    this.norgeApplicationService.submitApplication(application, this.fileUpload?.files ?? [])
      .pipe(finalize(() => this.spinner.hide()))
      .subscribe({
        next: (result) => {
          this.isSubmitted = true;
          this.router.navigate(['/no/soknad/kvittering'], { state: { referenceNumber: result.referenceNumber, summary } });
        },
        error: () => {
          this.errorModalService.open(
            this.translate.instant('modal.unknown-error'),
            this.translate.instant('error.could-not-save')
          );
        }
      });
  }

  public canDeactivate(): Observable<boolean> | boolean {
    if (this.isSubmitted || !this.hasUnsavedChanges) {
      return true;
    }

    const modalRef = this.modalService.open(ConfirmLeaveFormModalComponent, {
      centered: true,
      backdrop: 'static'
    });

    return from(modalRef.result.then((result) => result === true).catch(() => false));
  }

  @HostListener('window:beforeunload', ['$event'])
  public onBeforeUnload(event: BeforeUnloadEvent): void {
    if (!this.isSubmitted && this.hasUnsavedChanges) {
      event.preventDefault();
    }
  }

  private get hasUnsavedChanges(): boolean {
    return this.form.dirty || (this.fileUpload?.files.length ?? 0) > 0;
  }

  private buildSummaryRows(values: Record<string, unknown>, scope: string | null = null): SummaryRow[] {
    const rows: SummaryRow[] = [];

    for (const [key, value] of Object.entries(values)) {
      if (value === null || value === undefined || value === '' || (Array.isArray(value) && value.length === 0)) {
        continue;
      }

      if (Array.isArray(value) && typeof value[0] === 'object') {
        const rowLines = value.map((rowValues, rowIndex) => `${rowIndex + 1}. ${this.buildSummaryLines(rowValues, key).join(', ')}`);
        rows.push({ label: this.translate.instant(`norge.soknad.overskrift.${key}`), lines: rowLines });
      } else if (Array.isArray(value)) {
        const optionLabels = value.map(option => this.translate.instant(`norge.soknad.valg.${option}`));
        rows.push({ label: this.translateSummaryLabel(scope, key), lines: [optionLabels.join(', ')] });
      } else if (typeof value === 'object') {
        rows.push({ label: this.translate.instant(`norge.soknad.overskrift.${key}`), lines: this.buildSummaryLines(value as Record<string, unknown>, key) });
      } else {
        rows.push({ label: this.translateSummaryLabel(scope, key), lines: [this.formatSummaryValue(key, value)] });
      }
    }

    return rows;
  }

  private buildSummaryLines(values: Record<string, unknown>, scope: string): string[] {
    return Object.entries(values)
      .filter(([, value]) => value !== null && value !== undefined && value !== '')
      .map(([key, value]) => `${this.translateSummaryLabel(scope, key)}: ${this.formatSummaryValue(key, value)}`);
  }

  private translateSummaryLabel(scope: string | null, key: string): string {
    if (key === 'landSoker') {
      return this.translate.instant('label.country');
    }

    if (key === declarationControlName) {
      return this.translate.instant('norge.soknad.felt.erklaring');
    }

    const isForeignAddressVariant = addressFieldKeysWithForeignVariant.includes(key) && this.addressVariant === 'utenlandsk';
    const labelKey = isForeignAddressVariant ? `${key}Utenlandsk` : key;

    return this.translate.instant(scope ? `norge.soknad.felt.${scope}.${labelKey}` : `norge.soknad.felt.${labelKey}`);
  }

  private formatSummaryValue(key: string, value: unknown): string {
    if (typeof value === 'boolean') {
      return this.translate.instant(value ? 'button.ja' : 'button.nej');
    }

    if (typeof value === 'number') {
      return value.toLocaleString('nb-NO');
    }

    const text = String(value);
    const dateMatch = isoDatePattern.exec(text);

    if (dateMatch) {
      return `${dateMatch[3]}.${dateMatch[2]}.${dateMatch[1]}`;
    }

    if (key === 'landSoker') {
      return this.countries.find(country => country.code === text)?.name ?? text;
    }

    if (text === 'ja' || text === 'nei') {
      return this.translate.instant(text === 'ja' ? 'button.ja' : 'button.nej');
    }

    const optionKey = `norge.soknad.valg.${text}`;
    const translatedOption = this.translate.instant(optionKey);

    return translatedOption === optionKey ? text : translatedOption;
  }
}
